<template>
  <div class="admin-dashboard">
    <section class="admin-dashboard__hero mb-6">
      <div>
        <div class="text-overline primary--text font-weight-bold">Business overview</div>
        <h1 class="text-h4 text-sm-h3 font-weight-bold mb-2">Welcome back</h1>
        <p class="text-body-1 admin-muted mb-0">{{ todayLabel }}</p>
      </div>
      <div v-if="!isMobileApp" class="admin-dashboard__actions mt-4 mt-sm-0">
        <v-btn color="primary" depressed rounded to="/employee/list">
          <v-icon left>mdi-account-group-outline</v-icon>
          Manage employees
        </v-btn>
        <v-btn outlined color="primary" rounded to="/location/create">
          <v-icon left>mdi-store-plus-outline</v-icon>
          Add location
        </v-btn>
      </div>
    </section>

    <v-alert
      v-if="isNativeApp"
      :type="notificationAlertType"
      :outlined="notificationStatus !== 'enabled'"
      class="rounded-xl mb-6"
    >
      <div class="d-flex align-center flex-wrap">
        <div class="flex-grow-1 pr-3">
          <div class="font-weight-bold">{{ notificationTitle }}</div>
          <div class="text-body-2">{{ notificationMessage }}</div>
        </div>
        <v-btn
          v-if="notificationStatus !== 'enabled' && notificationStatus !== 'unavailable'"
          color="primary"
          depressed
          rounded
          class="mt-3 mt-sm-0"
          :loading="enablingNotifications"
          @click="enableNotifications"
        >
          {{ notificationStatus === 'denied' ? 'Try again' : 'Enable notifications' }}
        </v-btn>
      </div>
    </v-alert>

    <v-row class="mb-2">
      <v-col cols="12" sm="6" lg="4">
        <v-card outlined class="admin-summary-card rounded-xl pa-5" height="100%">
          <div class="d-flex align-center">
            <v-avatar color="primary" class="admin-summary-card__icon" size="48">
              <v-icon dark>mdi-storefront-outline</v-icon>
            </v-avatar>
            <div class="ml-4">
              <div class="text-h4 font-weight-bold">{{ locations.length }}</div>
              <div class="admin-muted">Locations in this organization</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="4">
        <v-card outlined class="admin-summary-card rounded-xl pa-5" height="100%">
          <div class="d-flex align-center">
            <v-avatar color="primary" class="admin-summary-card__icon" size="48">
              <v-icon dark>mdi-account-multiple-outline</v-icon>
            </v-avatar>
            <div class="ml-4">
              <div class="text-h4 font-weight-bold">{{ employeeCount === null ? '—' : employeeCount }}</div>
              <div class="admin-muted">Employees</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="4">
        <v-card outlined class="admin-summary-card rounded-xl pa-5" height="100%" to="/employee/list">
          <div class="d-flex align-center">
            <v-avatar color="primary" class="admin-summary-card__icon" size="48">
              <v-icon dark>mdi-account-cog-outline</v-icon>
            </v-avatar>
            <div class="ml-4">
              <div class="text-subtitle-1 font-weight-bold">Employee management</div>
              <div class="admin-muted">Manage access, roles, and locations</div>
            </div>
            <v-icon class="ml-auto">mdi-chevron-right</v-icon>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="admin-panel rounded-xl">
      <v-card-title class="admin-panel__header">
        <div>
          <h2 class="text-h6 font-weight-bold mb-1">Your locations</h2>
          <p class="text-body-2 admin-muted mb-0">Find a location and review its performance.</p>
        </div>
        <DateTimePicker
          class="admin-dashboard__date-picker"
          :defaultRange="dateTime.range"
          :range="true"
          v-model="dateTime"
          :initStartDate="start"
          :initEndDate="end"
          @submit="refreshLocationReports"
        />
      </v-card-title>

      <v-card-text class="pa-4 pa-sm-6">
        <v-text-field
          v-model="locationSearch"
          aria-label="Search locations"
          autocomplete="off"
          clearable
          color="primary"
          dense
          hide-details
          outlined
          rounded
          placeholder="Search by location or organization"
          prepend-inner-icon="mdi-magnify"
          class="admin-dashboard__search mb-6"
        />

        <v-row v-if="filteredLocations.length" align="start">
          <v-col
            v-for="(location, index) in filteredLocations"
            :key="location.id || index"
            cols="12"
            md="6"
            xl="4"
            class="align-self-start"
          >
            <v-card outlined class="admin-location-card rounded-xl">
              <v-card-title class="pb-2 align-start">
                <v-avatar color="primary" size="42" class="mr-3 admin-location-card__icon">
                  <v-icon dark>mdi-store-marker-outline</v-icon>
                </v-avatar>
                <div class="min-width-0">
                  <div
                    class="text-subtitle-1 font-weight-bold text-truncate"
                    v-html="$options.filters.highlight(location.name, locationSearch)"
                  />
                  <div class="text-caption admin-muted text-truncate">
                    {{ location.Organization && location.Organization.name }}
                  </div>
                  <div v-if="location.code" class="text-caption admin-muted mt-1">Code {{ location.code }}</div>
                  <div v-if="location.Address" class="text-caption admin-muted text-truncate mt-1">
                    {{ [location.Address.addressLine1, location.Address.city].filter(Boolean).join(', ') }}
                  </div>
                </div>
              </v-card-title>
              <v-card-actions class="admin-location-card__actions px-4 pt-2 pb-3">
                <v-btn text color="primary" :to="`/location/${location.id}/profile`">
                  View location
                </v-btn>
                <v-btn v-if="location.code" text color="primary" :href="getRegisterURL(location)" target="_blank" rel="noopener">
                  Open register
                </v-btn>
              </v-card-actions>
              <v-btn
                block
                tile
                text
                color="primary"
                class="admin-location-card__performance-toggle"
                :aria-expanded="expandedLocationId === location.id ? 'true' : 'false'"
                :aria-controls="`location-performance-${location.id}`"
                @click="toggleLocation(location.id)"
              >
                <v-icon left>{{ expandedLocationId === location.id ? 'mdi-chevron-up' : 'mdi-chart-line' }}</v-icon>
                {{ expandedLocationId === location.id ? 'Hide performance' : performanceActionLabel }}
              </v-btn>
              <v-expand-transition>
                <div
                  v-if="expandedLocationId === location.id"
                  :id="`location-performance-${location.id}`"
                  class="admin-location-card__report pa-3"
                >
                  <div class="px-3 pt-2">
                    <div class="text-subtitle-2 font-weight-bold">{{ performanceSectionTitle }}</div>
                    <div class="text-caption admin-muted">Income and payment activity for {{ performanceRangeLabel }}.</div>
                  </div>
                  <SaleBaseTileMini
                    ref="saleBaseTile"
                    :dateTime="dateTime"
                    :id="location.id"
                  />
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>
        </v-row>

        <div v-else class="admin-empty-state py-12 text-center">
          <v-icon size="54" color="primary">mdi-store-search-outline</v-icon>
          <h3 class="text-h6 mt-3">{{ locationSearch ? 'No matching locations' : 'No locations yet' }}</h3>
          <p class="admin-muted mb-4">{{ locationSearch ? 'Try a different location or organization name.' : 'Add a location to get started.' }}</p>
          <v-btn v-if="!locationSearch" color="primary" depressed rounded to="/location/create">Add location</v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DateTimePicker from "@/components/DateTimePicker";
import SaleBaseTileMini from "@/modules/Location/components/SaleBaseMini.vue";
import employeeService from "@/modules/Employee/service";
import { embeddedMobileApp, isNativeMobileApp } from "@/mobile/runtime";
import { enableMobileNotifications, getMobileNotificationStatus } from "@/mobile/service";

export default {
  name: "Home",
  components: { DateTimePicker, SaleBaseTileMini },
  data() {
    const startOfDay = this.$moment().startOf("day");
    const endOfDay = this.$moment().endOf("day");
    return {
      locations: [],
      locationSearch: "",
      expandedLocationId: null,
      employeeCount: null,
      notificationStatus: "prompt",
      enablingNotifications: false,
      start: startOfDay,
      end: endOfDay,
      dateTime: {
        range: "toDay",
        start: startOfDay.toISOString(),
        end: endOfDay.toISOString(),
      },
    };
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    isMobileApp() {
      return isNativeMobileApp || embeddedMobileApp.active;
    },
    isNativeApp() { return isNativeMobileApp; },
    todayLabel() {
      return this.$moment().format("dddd, MMMM D, YYYY");
    },
    filteredLocations() {
      const query = String(this.locationSearch || "").trim().toLowerCase();
      if (!query) return this.locations;
      return this.locations.filter((location) => {
        const organizationName = location.Organization && location.Organization.name;
        return [location.name, organizationName]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      });
    },
    performanceActionLabel() {
      return this.dateTime.range === "toDay" ? "View today's performance" : "View performance";
    },
    notificationAlertType() {
      if (this.notificationStatus === "enabled") return "success";
      if (this.notificationStatus === "error") return "error";
      if (this.notificationStatus === "unavailable") return "warning";
      return "info";
    },
    notificationTitle() {
      return this.notificationStatus === "enabled" ? "Notifications are enabled" : "Stay up to date";
    },
    notificationMessage() {
      if (this.notificationStatus === "enabled") return "This phone is ready to receive store alerts.";
      if (this.notificationStatus === "denied") return "Notifications are off. Allow them in your phone settings, then try again.";
      if (this.notificationStatus === "unavailable") return "Notifications are not available in this test version yet. You can continue using the app normally.";
      if (this.notificationStatus === "error") return "Notifications could not be enabled. Check your connection and try again.";
      return "Enable alerts so important store activity can reach this phone.";
    },
    performanceSectionTitle() {
      return this.dateTime.range === "toDay" ? "Today's sales and cash overview" : "Sales and cash overview";
    },
    performanceRangeLabel() {
      if (this.dateTime.range === "toDay") return "today";
      const start = this.$moment(this.dateTime.start).format("MMM D, YYYY");
      const end = this.$moment(this.dateTime.end).format("MMM D, YYYY");
      return start === end ? start : `${start}–${end}`;
    },
  },
  created() {
    this.getAllLocations();
    this.getEmployeeCount();
    if (this.isNativeApp) this.loadNotificationStatus();
  },
  methods: {
    async loadNotificationStatus() {
      try {
        this.notificationStatus = await getMobileNotificationStatus();
      } catch (error) {
        this.notificationStatus = "error";
      }
    },
    async enableNotifications() {
      this.enablingNotifications = true;
      try {
        this.notificationStatus = await enableMobileNotifications();
      } catch (error) {
        this.notificationStatus = "error";
      } finally {
        this.enablingNotifications = false;
      }
    },
    getAllLocations() {
      const organizations = (this.currentUser && this.currentUser.Organizations) || [];
      this.locations = organizations.reduce((locations, organization) => locations.concat(
        (organization.Locations || []).map((location) => ({
          ...location,
          Organization: { id: organization.id, name: organization.name },
        })),
      ), []);
    },
    toggleLocation(locationId) {
      this.expandedLocationId = this.expandedLocationId === locationId ? null : locationId;
    },
    async getEmployeeCount() {
      try {
        const response = await employeeService.getAll({ limit: 1, offset: 0 });
        this.employeeCount = response.count;
      } catch (error) {
        this.employeeCount = null;
      }
    },
    getRegisterURL(location) {
      const url = this.$store.getters["brand/brandConfig"].urls.terminal;
      return `${url}/login/location?code=${location.code}`;
    },
    refreshLocationReports() {
      const refs = this.$refs.saleBaseTile;
      const reports = !refs ? [] : (Array.isArray(refs) ? refs : [refs]);
      reports.forEach((report) => {
        if (report && report.loadData) report.loadData();
      });
    },
  },
};
</script>
